import { useState, useEffect, useCallback, useRef, type DragEvent, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import './ProjetoEditar.css';

type PapelAssociacao = 'visualizador' | 'supervisao' | 'controle';

interface Associacao {
  id: string;
  usuarioId?: string;
  usuarioNome?: string;
  usuarioEmail?: string;
  nome?: string;
  email?: string;
  papel: string;
  criadoEm?: string;
}

interface TitularInfo {
  id: string;
  nome: string;
  email: string;
}

interface UsuarioBusca {
  id: string;
  nome: string;
  email: string;
  tipo?: string;
}

interface ClienteOption {
  id: string;
  nome: string;
  email: string;
}

interface TipoEtapa {
  id: string;
  nome: string;
  descricao: string;
  ativo?: boolean;
}

interface EtapaExistente {
  id: string;
  tipo_etapa_id: string;
  tipo_etapa_nome: string;
  ordem: number;
  progresso: number;
}

interface ProjetoData {
  id: string;
  nome: string;
  endereco: string;
  dataInicio: string;
  dataPrevistaConclusao?: string;
  titularId?: string;
  titular?: { id: string; nome: string };
  tipo?: 'projeto' | 'obra';
}

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

function ProjetoEditar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Loading/error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Project data fields
  const [nome, setNome] = useState('');
  const [cep, setCep] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [endereco, setEndereco] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataConclusao, setDataConclusao] = useState('');
  const [titularId, setTitularId] = useState('');

  // Reference data
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [tiposEtapa, setTiposEtapa] = useState<TipoEtapa[]>([]);

  // Etapas state
  const [etapas, setEtapas] = useState<EtapaExistente[]>([]);

  // Participantes (associations) state
  const [associacoes, setAssociacoes] = useState<Associacao[]>([]);
  const [titularInfo, setTitularInfo] = useState<TitularInfo | null>(null);
  const [assocLoading, setAssocLoading] = useState(false);
  const [addingPapel, setAddingPapel] = useState<PapelAssociacao | null>(null);
  const [assocSearchText, setAssocSearchText] = useState('');
  const [assocSearchResults, setAssocSearchResults] = useState<UsuarioBusca[]>([]);

  // Two-panel DnD state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragSource, setDragSource] = useState<'available' | 'selected' | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [dragOverPanel, setDragOverPanel] = useState<'available' | 'selected' | null>(null);
  const dragCounterRef = useRef(0);

  // Load project data + reference data
  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);
        const [projetoData, etapasData, clientesData] = await Promise.all([
          api.get(`/empreendimentos/${id}`),
          api.get(`/empreendimentos/${id}/etapas`),
          api.get('/usuarios?tipo=Cliente&ativo=true'),
        ]);

        const proj = projetoData as ProjetoData;
        setNome(proj.nome || '');
        setEndereco(proj.endereco || '');
        setDataInicio(proj.dataInicio ? proj.dataInicio.split('T')[0] : '');
        setDataConclusao(proj.dataPrevistaConclusao ? proj.dataPrevistaConclusao.split('T')[0] : '');
        setTitularId(proj.titularId || proj.titular?.id || '');

        // Filtrar tipos de etapa pela categoria do empreendimento
        const categoria = proj.tipo || 'obra';
        const tiposData = await api.get(`/tipos-etapa?categoria=${categoria}`);

        setEtapas(
          (etapasData as EtapaExistente[]).sort((a, b) => a.ordem - b.ordem)
        );
        setClientes(clientesData as ClienteOption[]);
        setTiposEtapa(
          (tiposData as TipoEtapa[]).filter((t) => t.ativo !== false)
        );
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados do projeto');
      } finally {
        setLoading(false);
      }
    }
    if (id) loadAll();
  }, [id]);

  // CEP auto-search
  useEffect(() => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setCepLoading(true);
      setCepError('');
      fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
        .then((res) => res.json())
        .then((data: ViaCepResponse) => {
          if (data.erro) {
            setCepError('CEP não encontrado');
          } else {
            const parts = [data.logradouro, data.bairro].filter(Boolean).join(', ');
            const city = `${data.localidade}/${data.uf}`;
            setEndereco(parts ? `${parts} - ${city}` : city);
          }
        })
        .catch(() => setCepError('Erro ao buscar CEP'))
        .finally(() => setCepLoading(false));
    } else {
      setCepError('');
    }
  }, [cep]);

  // Available tipos for adding (not already in etapas)
  const tiposDisponiveis = tiposEtapa.filter(
    (t) => !etapas.some((e) => e.tipo_etapa_id === t.id)
  );

  // --- Participantes logic ---
  const fetchAssociacoes = useCallback(async () => {
    if (!id) return;
    try {
      setAssocLoading(true);
      const data = await api.get(`/empreendimentos/${id}/associacoes`);
      const assocArray = Array.isArray(data) ? data : (data.associacoes || []);
      setAssociacoes(assocArray);
      if (data.titular?.id) {
        setTitularInfo(data.titular);
      }
    } catch {
      // Silently ignore — associations are non-critical
    } finally {
      setAssocLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchAssociacoes();
  }, [id, fetchAssociacoes]);

  // Debounced search for users to add
  useEffect(() => {
    if (!assocSearchText || assocSearchText.length < 1) {
      setAssocSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await api.get(`/usuarios?ativo=true&busca=${encodeURIComponent(assocSearchText)}`);
        setAssocSearchResults(data as UsuarioBusca[]);
      } catch {
        setAssocSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [assocSearchText]);

  const papelLabels: Record<PapelAssociacao, string> = {
    visualizador: 'Clientes',
    supervisao: 'Arquitetos',
    controle: 'Assistentes',
  };

  const getAssocByPapel = (papel: PapelAssociacao) => {
    const matches = [papel, papel.charAt(0).toUpperCase() + papel.slice(1)];
    // Also handle "Supervisão" and "Controle" with accent/case
    if (papel === 'supervisao') matches.push('Supervisão');
    if (papel === 'controle') matches.push('Controle');
    if (papel === 'visualizador') matches.push('Visualizador');
    return associacoes.filter((a) => matches.includes(a.papel));
  };

  const getCriadorId = (lista: Associacao[]) => {
    if (lista.length === 0) return null;
    return lista.reduce((oldest, curr) =>
      (curr.criadoEm || '') < (oldest.criadoEm || '') ? curr : oldest
    ).id;
  };

  const getFilteredSearchResults = (papel: PapelAssociacao) => {
    const tipoCompativel: Record<PapelAssociacao, string> = {
      visualizador: 'Cliente',
      supervisao: 'Arquiteto',
      controle: 'Assistente',
    };
    const tipoEsperado = tipoCompativel[papel];
    const associadosIds = associacoes.map((a) => a.usuarioId).filter(Boolean);
    const excludeIds = [...associadosIds, titularInfo?.id].filter(Boolean) as string[];
    return assocSearchResults.filter(
      (u) => !excludeIds.includes(u.id) && (!tipoEsperado || u.tipo === tipoEsperado)
    );
  };

  const handleAddAssociacao = async (usuario: UsuarioBusca, papel: PapelAssociacao) => {
    if (!id) return;
    try {
      await api.post(`/empreendimentos/${id}/associacoes`, { usuarioId: usuario.id, papel });
      setAddingPapel(null);
      setAssocSearchText('');
      await fetchAssociacoes();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar associação');
    }
  };

  const handleRemoveAssociacao = async (assocId: string) => {
    if (!id) return;
    try {
      await api.delete(`/empreendimentos/${id}/associacoes/${assocId}`);
      await fetchAssociacoes();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao remover associação');
    }
  };

  // Add etapa via drag (drop on selected panel)
  const handleAddEtapa = async (tipoEtapaId: string) => {
    if (!id) return;
    setError('');
    try {
      await api.post(`/empreendimentos/${id}/etapas`, { tipoEtapaId });
      const etapasData = await api.get(`/empreendimentos/${id}/etapas`);
      setEtapas((etapasData as EtapaExistente[]).sort((a, b) => a.ordem - b.ordem));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar etapa');
    }
  };

  // Remove etapa via drag (drop on available panel, only if progresso = 0)
  const handleRemoveEtapa = async (etapaId: string) => {
    if (!id) return;
    const etapa = etapas.find((e) => e.id === etapaId);
    if (!etapa || etapa.progresso > 0) return;
    setError('');
    try {
      await api.delete(`/empreendimentos/${id}/etapas/${etapaId}`);
      setEtapas((prev) => prev.filter((e) => e.id !== etapaId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao remover etapa');
    }
  };

  // DnD: Drag start
  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, itemId: string, source: 'available' | 'selected') => {
    setDraggedId(itemId);
    setDragSource(source);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
  }, []);

  // DnD: Drag end
  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragSource(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  }, []);

  // DnD: Panel enter/leave
  const handlePanelDragEnter = useCallback((panel: 'available' | 'selected') => {
    dragCounterRef.current++;
    setDragOverPanel(panel);
  }, []);

  const handlePanelDragLeave = useCallback(() => {
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      setDragOverPanel(null);
      dragCounterRef.current = 0;
    }
  }, []);

  const handlePanelDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // DnD: Drop on "Disponíveis" panel (remove from project)
  const handleDropOnAvailable = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const etapaId = draggedId;
    if (etapaId && dragSource === 'selected') {
      const etapa = etapas.find((et) => et.id === etapaId);
      if (etapa && etapa.progresso === 0) {
        handleRemoveEtapa(etapaId);
      }
    }
    setDraggedId(null);
    setDragSource(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  };

  // DnD: Drop on "Selecionadas" panel (add to project)
  const handleDropOnSelected = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const itemId = draggedId;
    if (itemId && dragSource === 'available') {
      handleAddEtapa(itemId);
    }
    setDraggedId(null);
    setDragSource(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  };

  // DnD: Drop on specific item in "Selecionadas" (reorder or add+reorder)
  const handleDropOnItem = (e: DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const itemId = draggedId;
    if (!itemId) return;

    if (dragSource === 'available') {
      handleAddEtapa(itemId);
    } else if (dragSource === 'selected' && itemId !== targetId) {
      // Reorder within selected panel
      setEtapas((prev) => {
        const newList = [...prev];
        const fromIndex = newList.findIndex((et) => et.id === itemId);
        const toIndex = newList.findIndex((et) => et.id === targetId);
        if (fromIndex === -1 || toIndex === -1) return prev;
        const [moved] = newList.splice(fromIndex, 1);
        newList.splice(toIndex, 0, moved);
        return newList.map((item, idx) => ({ ...item, ordem: idx + 1 }));
      });
    }

    setDraggedId(null);
    setDragSource(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  };

  // Save all changes
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      // Update project data
      await api.put(`/empreendimentos/${id}`, {
        nome,
        endereco,
        dataInicio,
        dataPrevistaConclusao: dataConclusao,
        titularId: titularId || undefined,
      });

      // Reorder etapas
      if (etapas.length > 0) {
        await api.patch(`/empreendimentos/${id}/etapas/reorder`, {
          etapas: etapas.map((e, idx) => ({ id: e.id, ordem: idx + 1 })),
        });
      }

      navigate(`/empreendimentos/${id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="projeto-editar">
        <div className="projeto-editar-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="projeto-editar">
      <div className="projeto-editar-container">
        <Link to={`/empreendimentos/${id}`} className="projeto-editar-back">
          ← Voltar ao projeto
        </Link>

        <header className="projeto-editar-header">
          <h1>Editar Projeto</h1>
          <p className="projeto-editar-subtitle">Altere os dados e a ordem das etapas</p>
        </header>

        {error && (
          <p style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>{error}</p>
        )}
        {successMsg && (
          <p style={{ color: '#4caf50', textAlign: 'center', margin: '1rem 0' }}>{successMsg}</p>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Section 1: Dados do Projeto */}
          <section className="editar-section">
            <h2>Dados do Projeto</h2>

            <div className="form-group">
              <label htmlFor="edit-nome">Nome do Projeto</label>
              <input
                id="edit-nome"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do projeto"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-cep">CEP (buscar endereço)</label>
              <div className="editar-cep-wrapper">
                <input
                  id="edit-cep"
                  type="text"
                  value={cep}
                  onChange={(e) => setCep(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Digite o CEP para buscar"
                  maxLength={8}
                  className={cepError ? 'error' : ''}
                />
                {cepLoading && <span className="editar-cep-loading">Buscando...</span>}
              </div>
              <span className="form-error">{cepError || ''}</span>
            </div>

            <div className="form-group">
              <label htmlFor="edit-endereco">Endereço</label>
              <input
                id="edit-endereco"
                type="text"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                placeholder="Endereço completo"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-data-inicio">Data de Início</label>
              <input
                id="edit-data-inicio"
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-data-conclusao">Data Prevista de Conclusão</label>
              <input
                id="edit-data-conclusao"
                type="date"
                value={dataConclusao}
                onChange={(e) => setDataConclusao(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-titular">Titular (Cliente)</label>
              <select
                id="edit-titular"
                value={titularId}
                onChange={(e) => setTitularId(e.target.value)}
              >
                <option value="">— Selecionar titular —</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} ({c.email})
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* Section 2: Etapas (Two-panel Drag and Drop) */}
          <section className="editar-section">
            <h2>Etapas</h2>
            <p style={{ color: '#999', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Arraste da esquerda para a direita para adicionar. Reordene arrastando dentro da lista. Etapas com progresso não podem ser removidas.
            </p>

            <div className="editar-dnd-container">
              {/* Left panel: Disponíveis */}
              <div
                className={`editar-dnd-panel ${dragOverPanel === 'available' ? 'drag-over' : ''}`}
                onDragEnter={() => handlePanelDragEnter('available')}
                onDragLeave={handlePanelDragLeave}
                onDragOver={handlePanelDragOver}
                onDrop={handleDropOnAvailable}
              >
                <h3 className="editar-dnd-panel-title">Disponíveis</h3>
                <div className="editar-dnd-list">
                  {tiposDisponiveis.map((tipo) => (
                    <div
                      key={tipo.id}
                      className={`editar-dnd-item ${draggedId === tipo.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, tipo.id, 'available')}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="editar-dnd-handle">☰</span>
                      <div className="editar-dnd-item-info">
                        <div className="editar-dnd-item-name">{tipo.nome}</div>
                        <div className="editar-dnd-item-detail">{tipo.descricao}</div>
                      </div>
                    </div>
                  ))}
                  {tiposDisponiveis.length === 0 && (
                    <p className="editar-dnd-empty">Todas as etapas foram adicionadas</p>
                  )}
                </div>
              </div>

              {/* Right panel: Selecionadas (ordem) */}
              <div
                className={`editar-dnd-panel ${dragOverPanel === 'selected' ? 'drag-over' : ''}`}
                onDragEnter={() => handlePanelDragEnter('selected')}
                onDragLeave={handlePanelDragLeave}
                onDragOver={handlePanelDragOver}
                onDrop={handleDropOnSelected}
              >
                <h3 className="editar-dnd-panel-title">Selecionadas (ordem)</h3>
                <div className="editar-dnd-list">
                  {etapas.map((etapa, index) => {
                    const isLocked = etapa.progresso > 0;
                    // Locked items can be reordered but not dragged back to available
                    return (
                      <div
                        key={etapa.id}
                        className={`editar-dnd-item selected ${draggedId === etapa.id ? 'dragging' : ''} ${dragOverTarget === etapa.id ? 'drop-target' : ''} ${isLocked ? 'locked' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, etapa.id, 'selected')}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverTarget(etapa.id);
                        }}
                        onDragLeave={() => setDragOverTarget(null)}
                        onDrop={(e) => handleDropOnItem(e, etapa.id)}
                      >
                        <span className="editar-dnd-order">{index + 1}.</span>
                        <span className="editar-dnd-handle">☰</span>
                        <div className="editar-dnd-item-info">
                          <div className="editar-dnd-item-name">
                            {isLocked && <span className="editar-dnd-lock">🔑</span>}
                            {etapa.tipo_etapa_nome}
                          </div>
                          <div className="editar-dnd-progresso">
                            <div className="editar-dnd-progresso-bar">
                              <div
                                className="editar-dnd-progresso-fill"
                                style={{ width: `${etapa.progresso}%` }}
                              />
                            </div>
                            <span className="editar-dnd-progresso-text">{etapa.progresso}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {etapas.length === 0 && (
                    <p className="editar-dnd-empty">Arraste etapas para cá</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Participantes */}
          <section className="editar-section">
            <h2>Participantes</h2>
            <p style={{ color: '#999', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Gerencie os participantes do projeto. Alterações são salvas automaticamente.
            </p>

            {assocLoading ? (
              <p style={{ color: '#c9a96e', textAlign: 'center' }}>Carregando participantes...</p>
            ) : (
              (['visualizador', 'supervisao', 'controle'] as PapelAssociacao[]).map((papel) => {
                const lista = getAssocByPapel(papel);
                const criadorId = papel === 'supervisao' ? getCriadorId(lista) : null;
                const sortedLista = criadorId
                  ? [...lista].sort((a, b) => (a.id === criadorId ? -1 : b.id === criadorId ? 1 : 0))
                  : lista;
                const count = papel === 'visualizador' && titularInfo ? lista.length + 1 : lista.length;

                return (
                  <div className="participantes-grupo" key={papel}>
                    <div className="participantes-grupo-header">
                      <div className="participantes-grupo-title">
                        <span>{papelLabels[papel]}</span>
                        <span className="participantes-badge">{count}</span>
                      </div>
                      <button
                        type="button"
                        className="participantes-add-btn"
                        onClick={() => {
                          setAddingPapel(addingPapel === papel ? null : papel);
                          setAssocSearchText('');
                          setAssocSearchResults([]);
                        }}
                      >
                        + Adicionar
                      </button>
                    </div>

                    {/* Inline autocomplete search */}
                    {addingPapel === papel && (
                      <div className="participantes-autocomplete">
                        <input
                          type="text"
                          className="participantes-autocomplete-input"
                          placeholder="Buscar usuário por nome ou e-mail..."
                          value={assocSearchText}
                          onChange={(e) => setAssocSearchText(e.target.value)}
                          autoFocus
                          aria-label={`Buscar usuário para ${papelLabels[papel]}`}
                        />
                        {assocSearchText.length >= 1 && (
                          <div className="participantes-autocomplete-dropdown">
                            {getFilteredSearchResults(papel).map((usuario) => (
                              <button
                                key={usuario.id}
                                type="button"
                                className="participantes-autocomplete-option"
                                onClick={() => handleAddAssociacao(usuario, papel)}
                              >
                                <span className="participantes-option-name">{usuario.nome}</span>
                                <span className="participantes-option-email">{usuario.email}</span>
                              </button>
                            ))}
                            {getFilteredSearchResults(papel).length === 0 && (
                              <div className="participantes-autocomplete-empty">
                                Nenhum usuário encontrado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* User list */}
                    <div className="participantes-list">
                      {/* Titular shown in Clientes group */}
                      {papel === 'visualizador' && titularInfo && (
                        <div className="participantes-user-item participantes-user-locked">
                          <div className="participantes-user-info">
                            <div className="participantes-user-nome">
                              <span title="Titular do projeto">🔑</span>
                              {titularInfo.nome}
                              <span className="participantes-lock-badge">Titular</span>
                            </div>
                            <div className="participantes-user-email">{titularInfo.email}</div>
                          </div>
                        </div>
                      )}

                      {sortedLista.map((assoc) => {
                        const isCriador = assoc.id === criadorId;
                        const nome = assoc.usuarioNome || assoc.nome || '';
                        const email = assoc.usuarioEmail || assoc.email || '';
                        return (
                          <div
                            className={`participantes-user-item ${isCriador ? 'participantes-user-locked' : ''}`}
                            key={assoc.id}
                          >
                            <div className="participantes-user-info">
                              <div className="participantes-user-nome">
                                {isCriador && <span title="Criador do projeto">🔑</span>}
                                {nome}
                                {isCriador && <span className="participantes-lock-badge">Principal</span>}
                              </div>
                              <div className="participantes-user-email">{email}</div>
                            </div>
                            {!isCriador && (
                              <button
                                type="button"
                                className="participantes-remove-btn"
                                onClick={() => handleRemoveAssociacao(assoc.id)}
                                aria-label={`Remover ${nome}`}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {lista.length === 0 && !(papel === 'visualizador' && titularInfo) && (
                        <div className="participantes-empty">
                          Nenhum usuário associado como {papelLabels[papel].toLowerCase()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </section>

          {/* Save button */}
          <div className="editar-actions">
            <button type="submit" className="btn-salvar" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjetoEditar;
